import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { format } from 'date-fns';
import { Download, Eye, FileText, Flag, FolderTree, LockKeyhole, Trash2 } from 'lucide-react';

import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import type { Milestone, RepoFile } from '@/pages/developer/types';

export type CodeSnapshotCardProps = {
  repoFiles: RepoFile[];
  sortedRepoFiles: RepoFile[];
  repoFilesUpdatedAt: string | null;
  repoFilesError: string | null;
  repoFilesLoading: boolean;
  repoFileCount: number | null;

  repoSnapshotVersion: string | null;
  repoSnapshotCommitSha: string | null;

  developerUnlocked: boolean;
  isAdminUser: boolean;

  selectedRepoFile: RepoFile | null;
  setSelectedRepoFile: Dispatch<SetStateAction<RepoFile | null>>;

  beginDownloadRepoZip: () => void;

  milestones: Milestone[];
  milestonesLoading: boolean;
  milestonesError: string | null;

  milestoneTargetCommitSha: string | null;

  milestoneDialogOpen: boolean;
  setMilestoneDialogOpen: Dispatch<SetStateAction<boolean>>;
  milestoneTitle: string;
  setMilestoneTitle: Dispatch<SetStateAction<string>>;
  milestoneSaving: boolean;
  createMilestone: () => Promise<void>;

  deleteSnapshotDialogOpen: boolean;
  setDeleteSnapshotDialogOpen: Dispatch<SetStateAction<boolean>>;
  deleteSnapshotLoading: boolean;
  deleteSnapshot: () => Promise<void>;

  onJumpToPublishedBuilds?: () => void;
  onViewCommit?: (commitSha: string) => void;
};

export function CodeSnapshotCard({
  repoFiles,
  sortedRepoFiles,
  repoFilesUpdatedAt,
  repoFilesError,
  repoFilesLoading,
  repoFileCount,
  repoSnapshotVersion,
  repoSnapshotCommitSha,
  developerUnlocked,
  isAdminUser,
  selectedRepoFile,
  setSelectedRepoFile,
  beginDownloadRepoZip,
  milestones,
  milestonesLoading,
  milestonesError,
  milestoneTargetCommitSha,
  milestoneDialogOpen,
  setMilestoneDialogOpen,
  milestoneTitle,
  setMilestoneTitle,
  milestoneSaving,
  createMilestone,
  deleteSnapshotDialogOpen,
  setDeleteSnapshotDialogOpen,
  deleteSnapshotLoading,
  deleteSnapshot,
  onJumpToPublishedBuilds,
  onViewCommit,
}: CodeSnapshotCardProps) {
  const [deleteSnapshotConfirmation, setDeleteSnapshotConfirmation] = useState('');

  const canConfirmDeleteSnapshot = deleteSnapshotConfirmation.trim().toLowerCase() === 'delete';

  const milestoneCommitSha = (milestoneTargetCommitSha ?? repoSnapshotCommitSha ?? '').trim();
  const milestoneForCommit =
    milestoneCommitSha.length > 0
      ? milestones.find((milestone) => (milestone.toCommitSha ?? '').trim() === milestoneCommitSha) ?? null
      : null;
  const isEditingMilestone = !!milestoneForCommit;

  const normalizedSnapshotCommitSha = (repoSnapshotCommitSha ?? '').trim();
  const previousMilestoneEndSha = (milestones[0]?.toCommitSha ?? '').trim();
  const latestMilestone = milestones.reduce<Milestone | null>((acc, entry) => {
    const entryTime = new Date(entry.createdAt).getTime();
    if (Number.isNaN(entryTime)) return acc;
    if (!acc) return entry;
    const accTime = new Date(acc.createdAt).getTime();
    if (Number.isNaN(accTime)) return entry;
    return entryTime > accTime ? entry : acc;
  }, null);
  const latestMilestoneHasSnapshot = !!latestMilestone?.snapshotVersion;
  const isShowingLatestMilestoneSnapshot =
    latestMilestoneHasSnapshot &&
    normalizedSnapshotCommitSha.length > 0 &&
    (latestMilestone?.toCommitSha ?? '').trim() === normalizedSnapshotCommitSha;

  const displayFileCount = repoFiles.length > 0 ? repoFiles.length : typeof repoFileCount === 'number' ? repoFileCount : 0;
  const isSnapshotKnown = !!repoSnapshotCommitSha || !!repoSnapshotVersion || !!repoFilesUpdatedAt || (typeof repoFileCount === 'number' && repoFileCount > 0);
  const hasMilestoneSnapshotButNotLoaded =
    latestMilestoneHasSnapshot &&
    !!latestMilestone &&
    !!(latestMilestone.toCommitSha ?? '').trim() &&
    (!normalizedSnapshotCommitSha || (latestMilestone.toCommitSha ?? '').trim() !== normalizedSnapshotCommitSha || sortedRepoFiles.length === 0);

  return (
    <Card id="code-snapshot" className="mb-8">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FolderTree className="w-5 h-5" />
              Code Snapshot (Sanitized)
            </CardTitle>
            <CardDescription>
              Stored in Firestore only after “Generate snapshot” / “Refresh code snapshot”. Saved as{' '}
              {repoSnapshotVersion || 'V1.0... (commit)'}. Preview is available to admins; others must unlock with the developer passcode.
            </CardDescription>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-2 text-xs text-muted-foreground text-left sm:text-right">
            <span>{displayFileCount} files</span>
            <span>
              {repoFilesLoading
                ? 'Loading…'
                : repoFilesUpdatedAt
                  ? `Updated ${format(new Date(repoFilesUpdatedAt), 'MMM d, yyyy · h:mm a')}`
                  : isSnapshotKnown
                    ? 'Snapshot metadata loaded'
                    : 'Not synced'}
            </span>
            {repoSnapshotVersion && <span>{repoSnapshotVersion}</span>}
            {isShowingLatestMilestoneSnapshot && latestMilestone && (
              <span>
                Latest milestone: <span className="font-medium text-foreground">{latestMilestone.title}</span>
              </span>
            )}

            {developerUnlocked && repoFiles.length > 0 && (
              <div className="flex w-full flex-col sm:flex-row sm:flex-wrap justify-start sm:justify-end gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  className="w-full sm:w-auto gap-2 text-xs"
                  disabled={deleteSnapshotLoading}
                  onClick={() => setDeleteSnapshotDialogOpen(true)}
                >
                  <Trash2 className="w-4 h-4" />
                  Delete snapshot
                </Button>
              </div>
            )}

          </div>
        </div>
      </CardHeader>
      <CardContent>
        {(developerUnlocked || isAdminUser) && (
          <div className="mb-6 rounded-lg border border-border/60 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-medium">Milestones</p>
                <p className="text-xs text-muted-foreground mt-1">Groups commits since the previous milestone.</p>
              </div>
              {developerUnlocked && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="w-full sm:w-auto gap-2 text-xs"
                  onClick={() => {
                    setMilestoneTitle(milestoneForCommit?.title ?? '');
                    setMilestoneDialogOpen(true);
                  }}
                >
                  <Flag className="w-4 h-4" />
                  {isEditingMilestone ? 'Edit milestone' : 'Add milestone'}
                </Button>
              )}
            </div>

            {!developerUnlocked && milestones.length > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">Developer passcode required to add or edit milestones.</p>
            )}

            {milestonesError && (
              <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
                {milestonesError}
              </div>
            )}

            {milestonesLoading ? (
              <p className="mt-3 text-xs text-muted-foreground">Loading milestones…</p>
            ) : milestones.length === 0 ? (
              <p className="mt-3 text-xs text-muted-foreground">No milestones yet.</p>
            ) : (
              <div className="mt-3 rounded-lg border border-border overflow-hidden">
                <div className="sm:hidden">
                  <div className="divide-y divide-border">
                    {milestones.map((entry) => (
                      <div key={entry.id} className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium break-words">{entry.title}</p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {Number.isNaN(new Date(entry.createdAt).getTime())
                                ? '—'
                                : format(new Date(entry.createdAt), 'MMM d, yyyy · h:mm a')}
                            </p>
                            <p className="mt-2 text-xs text-muted-foreground">
                              Version: <span className="font-mono text-foreground">{entry.snapshotVersion ?? '—'}</span>
                            </p>
                            <div className="mt-2 text-xs text-muted-foreground">
                              Commit:{' '}
                              <span className="font-mono">
                                {onViewCommit ? (
                                  <Button
                                    type="button"
                                    variant="link"
                                    size="sm"
                                    className="h-auto px-0 py-0 font-mono text-xs"
                                    onClick={() => onViewCommit(entry.toCommitSha)}
                                  >
                                    {entry.toCommitSha.slice(0, 7)}
                                  </Button>
                                ) : (
                                  entry.toCommitSha.slice(0, 7)
                                )}
                              </span>
                            </div>
                          </div>
                          <div className="shrink-0 text-right">
                            <p className="text-xs text-muted-foreground">Commits</p>
                            <p className="text-sm font-semibold">{entry.commitCount}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="hidden sm:block overflow-x-auto">
                  <Table className="min-w-[820px]">
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead>Date</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>Version</TableHead>
                        <TableHead>Commit</TableHead>
                        <TableHead className="text-right">Commits</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {milestones.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="text-xs text-muted-foreground">
                            {Number.isNaN(new Date(entry.createdAt).getTime())
                              ? '—'
                              : format(new Date(entry.createdAt), 'MMM d, yyyy · h:mm a')}
                          </TableCell>
                          <TableCell className="text-xs">{entry.title}</TableCell>
                          <TableCell className="text-xs font-mono">{entry.snapshotVersion ?? '—'}</TableCell>
                          <TableCell className="text-xs font-mono">
                            {onViewCommit ? (
                              <Button
                                type="button"
                                variant="link"
                                size="sm"
                                className="h-auto px-0 py-0 font-mono text-xs"
                                onClick={() => onViewCommit(entry.toCommitSha)}
                              >
                                {entry.toCommitSha.slice(0, 7)}
                              </Button>
                            ) : (
                              entry.toCommitSha.slice(0, 7)
                            )}
                          </TableCell>
                          <TableCell className="text-right text-xs font-mono">{entry.commitCount}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}

        {repoFilesError && (
          <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
            {repoFilesError}
          </div>
        )}

        {sortedRepoFiles.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>{repoFilesLoading ? 'Loading code snapshot…' : 'No code snapshot available yet.'}</p>

            {hasMilestoneSnapshotButNotLoaded && latestMilestone ? (
              <div className="mt-2 space-y-2">
                <p className="text-xs">
                  Latest milestone <span className="font-medium text-foreground">{latestMilestone.title}</span> has snapshot version{' '}
                  <span className="font-mono text-foreground">{latestMilestone.snapshotVersion}</span>, but the active stored snapshot isn’t loaded.
                </p>
                <p className="text-xs">
                  {isAdminUser
                    ? 'Go to Published Builds and click “Generate snapshot” for that milestone commit to load/refresh the active snapshot.'
                    : 'Ask an admin to generate the snapshot from Published Builds, or unlock with the developer passcode and generate it in the Developer Dialog.'}
                </p>
                {isAdminUser && onJumpToPublishedBuilds && (
                  <div className="flex justify-center">
                    <Button type="button" size="sm" variant="outline" onClick={onJumpToPublishedBuilds}>
                      Go to Published Builds
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs">
                {isAdminUser
                  ? 'Generate a snapshot from Published Builds to preview sanitized files.'
                  : 'Unlock with the developer passcode, then generate the snapshot in the Developer Dialog.'}
              </p>
            )}
          </div>
        ) : developerUnlocked || isAdminUser ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="rounded-lg border border-border/60 p-3 max-h-[420px] overflow-y-auto">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">File tree</p>
              <div className="space-y-1">
                {sortedRepoFiles.map((file) => (
                  <button
                    key={file.path}
                    type="button"
                    onClick={() => setSelectedRepoFile(file)}
                    className={`w-full text-left text-xs px-2 py-1 rounded-md transition whitespace-normal break-all ${selectedRepoFile?.path === file.path ? 'bg-primary/10 text-primary' : 'hover:bg-muted/40'}`}
                  >
                    {file.path}
                  </button>
                ))}
              </div>
            </div>
            <div className="lg:col-span-2 rounded-lg border border-border/60 p-4 space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex items-center gap-2 text-sm font-medium">
                  <Eye className="w-4 h-4" />
                  <span className="truncate" title={selectedRepoFile?.path ?? undefined}>
                    {selectedRepoFile?.path ?? 'Select a file'}
                  </span>
                </div>
                <Button onClick={beginDownloadRepoZip} variant="outline" className="w-full sm:w-auto gap-2 text-xs">
                  <Download className="w-4 h-4" />
                  Download ZIP
                </Button>
              </div>
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-muted-foreground">
                Sensitive keys are automatically redacted from preview and downloads.
              </div>
              <pre className="max-h-[320px] overflow-auto rounded-md bg-muted/30 p-3 text-xs text-foreground whitespace-pre-wrap">
                {selectedRepoFile?.content ?? 'Select a file from the tree to preview its sanitized contents.'}
              </pre>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-border/60 bg-muted/20 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <LockKeyhole className="w-4 h-4" />
                Preview locked. Unlock the Developer Dialog to view files.
              </div>
              <Button onClick={beginDownloadRepoZip} variant="outline" className="gap-2 text-xs">
                <Download className="w-4 h-4" />
                Download ZIP
              </Button>
            </div>
            <div className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-muted-foreground">
              Sensitive keys are automatically redacted from downloads.
            </div>
          </div>
        )}

        <AlertDialog
          open={deleteSnapshotDialogOpen}
          onOpenChange={(open) => {
            setDeleteSnapshotDialogOpen(open);
            if (!open) setDeleteSnapshotConfirmation('');
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete stored code snapshot?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently deletes the saved sanitized snapshot fields from Firestore. Download history is not changed.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Type <span className="font-mono">delete</span> to confirm.</p>
              <Input
                value={deleteSnapshotConfirmation}
                onChange={(event) => setDeleteSnapshotConfirmation(event.target.value)}
                placeholder="delete"
                disabled={deleteSnapshotLoading}
              />
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteSnapshotLoading}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  await deleteSnapshot();
                  setDeleteSnapshotDialogOpen(false);
                }}
                disabled={deleteSnapshotLoading || !canConfirmDeleteSnapshot}
              >
                {deleteSnapshotLoading ? 'Deleting…' : 'Delete snapshot'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog open={milestoneDialogOpen} onOpenChange={setMilestoneDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{isEditingMilestone ? 'Edit milestone' : 'Add milestone'}</DialogTitle>
              <DialogDescription>
                {isEditingMilestone
                  ? 'Updates the milestone title for this snapshot commit. To create a future milestone, first generate a snapshot for a later commit, then add a milestone there.'
                  : 'Creates a milestone at the current snapshot commit and tags commits since the previous milestone. Future milestones will start from this commit.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Title</p>
                <Input
                  value={milestoneTitle}
                  onChange={(event) => setMilestoneTitle(event.target.value)}
                  placeholder={`Milestone ${format(new Date(), 'yyyy-MM-dd')}`}
                />
              </div>
              <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-xs text-muted-foreground space-y-1">
                <p>
                  Snapshot version: <span className="font-mono">{repoSnapshotVersion ?? '—'}</span>
                </p>
                <p>
                  Milestone commit:{' '}
                  <span className="font-mono" title={milestoneCommitSha || undefined}>
                    {milestoneCommitSha ? milestoneCommitSha.slice(0, 7) : '—'}
                  </span>
                </p>
                <p>
                  Snapshot commit:{' '}
                  <span className="font-mono" title={normalizedSnapshotCommitSha || undefined}>
                    {normalizedSnapshotCommitSha ? normalizedSnapshotCommitSha.slice(0, 7) : '—'}
                  </span>
                </p>
                <p>
                  Previous milestone (range end):{' '}
                  <span className="font-mono" title={previousMilestoneEndSha || undefined}>
                    {previousMilestoneEndSha ? previousMilestoneEndSha.slice(0, 7) : '—'}
                  </span>
                </p>
                {!isEditingMilestone && (
                  <p>
                    Tip: To create the next milestone later, generate a snapshot on that later commit first.
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMilestoneDialogOpen(false)} disabled={milestoneSaving}>
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  await createMilestone();
                  setMilestoneDialogOpen(false);
                }}
                disabled={milestoneSaving}
                className="gap-2"
              >
                <Flag className={`w-4 h-4 ${milestoneSaving ? 'animate-pulse' : ''}`} />
                {milestoneSaving ? 'Saving…' : isEditingMilestone ? 'Save changes' : 'Save milestone'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
